/**
 * Retry timing and classification for outbound requests.
 *
 * Full jitter — `random(0, min(cap, base * 2^n))` — rather than a fixed or linear delay. A
 * notification send sheds many clients within the same second, and without jitter they all
 * re-attempt on the same schedule, arriving as a second synchronised burst at a service that is
 * already saturated. Randomising the wait is what turns a herd back into a distribution.
 *
 * The iOS and Android SDKs implement the same policy with the same constants and the same
 * classification table. Keep them in step — a divergence here is invisible from any one codebase.
 */

/** Delay ceiling for the first retry. Doubles per attempt. */
const BASE_MILLIS = 500

/** No single wait exceeds this, however many attempts have been made. */
const CAP_MILLIS = 10_000

/**
 * Proportion of a server-specified `Retry-After` added on top as jitter, so every client that
 * received the same header does not come back in the same instant.
 */
const RETRY_AFTER_JITTER = 0.2

/**
 * Statuses whose `Retry-After` is honoured in place of the computed backoff. A shed or throttled
 * response is the only case where the server knows better than the client when to come back.
 */
const RETRY_AFTER_STATUSES: readonly number[] = [429, 503]

/**
 * Total attempts including the first. Deliberately small: the event queue re-attempts on its own
 * flush timer, so this loop only needs to ride out a brief blip rather than guarantee delivery.
 *
 * `Globals._eventMaxSendAttempts` is the queue's per-event budget and must hold the same number.
 */
export const MAX_ATTEMPTS = 4

/**
 * Milliseconds to wait before retry `attempt` (1-based: 1 is the first retry, not the first
 * request). Ranges are 0–500ms, 0–1s then 0–2s, for a worst case of 3.5s added latency.
 *
 * `retryAfterSeconds` is the delay the server asked for, when it asked for one; it takes
 * precedence over the computed backoff, clamped so a mistaken or hostile value cannot park an
 * event for minutes.
 */
export function retryDelayMillis(attempt: number, retryAfterSeconds?: number): number {
    if (retryAfterSeconds !== undefined) {
        const requestedMillis = Math.min(retryAfterSeconds * 1000, CAP_MILLIS)
        return requestedMillis + Math.random() * requestedMillis * RETRY_AFTER_JITTER
    }

    const ceilingMillis = Math.min(CAP_MILLIS, BASE_MILLIS * 2 ** (attempt - 1))
    return Math.random() * ceilingMillis
}

/**
 * Whether a response status is worth re-sending.
 *
 * `undefined` means the request never produced a response — a network, DNS or connection failure,
 * or a timeout abort — which is the most retryable case of all. Everything not listed is treated
 * as permanent: a request the server rejected on its merits will be rejected identically next
 * time, and spending the attempt budget on it only delays the events queued behind it.
 */
export function isRetryable(status: number | undefined): boolean {
    if (status === undefined) {
        return true
    }

    if (status === 408 || status === 429) {
        return true
    }

    return status >= 500 && status <= 599
}

/**
 * The delay a response asked to be retried after, in seconds, or `undefined` when it did not ask
 * for one that this policy honours.
 *
 * Only the delay-seconds form is read. The HTTP-date form is treated as absent and the computed
 * backoff is used instead, which keeps header parsing identical across the three SDKs and keeps a
 * clock-skewed client from computing a nonsense wait.
 */
export function retryAfterSecondsFrom(status: number, headerValue: string | null): number | undefined {
    if (headerValue === null || !RETRY_AFTER_STATUSES.includes(status)) {
        return undefined
    }

    if (!headerValue.trim()) {
        return undefined
    }

    const seconds = Number(headerValue)
    if (!Number.isFinite(seconds) || seconds < 0) {
        return undefined
    }

    return seconds
}
